import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { LogOut, Package, ClipboardList, RefreshCw, Users, Briefcase, CalendarDays, ShieldCheck } from "lucide-react";
import { TabBtn } from "@/components/admin/AdminBits";
import { QuotesPanel } from "@/components/admin/QuotesPanel";
import { ShipmentsPanel } from "@/components/admin/ShipmentsPanel";
import { ClientsPanel } from "@/components/admin/ClientsPanel";
import { CareersPanel } from "@/components/admin/CareersPanel";
import { AppointmentsPanel } from "@/components/admin/AppointmentsPanel";
import { StaffPanel } from "@/components/admin/StaffPanel";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);

  const logout = useCallback(async () => {
    try {
      await api.post("/admin/logout");
    } catch (error) {
      console.error("Admin logout failed:", error);
    }
    nav("/admin/login");
  }, [nav]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: user } = await api.get("/admin/me");
      setMe(user);
      if (user.role !== "admin") {
        setTab("appointments");
        return;
      }
      const [q, s, c, k] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/shipments"),
        api.get("/admin/clients"),
        api.get("/admin/careers"),
      ]);
      setQuotes(q.data);
      setShipments(s.data);
      setClients(c.data);
      setCareers(k.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        console.error("Admin data load failed:", e);
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const updateQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Quote updated");
    } catch (error) {
      console.error("Quote update failed:", error);
      toast.error("Update failed");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    try {
      await api.delete(`/admin/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Deleted");
    } catch (error) {
      console.error("Quote delete failed:", error);
      toast.error("Delete failed");
    }
  };

  const updateShipmentStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/shipments/${id}/status`, { status });
      setShipments((ls) => ls.map((s) => (s.id === id ? data : s)));
      toast.success("Status updated");
    } catch (error) {
      console.error("Shipment update failed:", error);
      toast.error("Update failed");
    }
  };

  const deleteShipment = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    try {
      await api.delete(`/admin/shipments/${id}`);
      setShipments((ls) => ls.filter((s) => s.id !== id));
      toast.success("Deleted");
    } catch (error) {
      console.error("Shipment delete failed:", error);
      toast.error("Delete failed");
    }
  };

  const updateCareerStatus = async (id, status) => {
    try {
      await api.patch(`/admin/careers/${id}`, { status });
      setCareers((ls) => ls.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success("Application updated");
    } catch (error) {
      console.error("Application update failed:", error);
      toast.error("Update failed");
    }
  };

  const deleteCareer = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/careers/${id}`);
      setCareers((ls) => ls.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (error) {
      console.error("Application delete failed:", error);
      toast.error("Delete failed");
    }
  };

  const isFullAdmin = me?.role === "admin";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand">Dashboard</div>
            <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">
              {isFullAdmin ? "Dispatcher Console" : "Appointment Portal"}
            </h1>
            {me && <div className="text-sm text-slate-600 mt-1">{me.name} · {me.email} · <span className="uppercase text-xs tracking-wider font-bold text-primary-brand">{me.role}</span></div>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={refreshAll} data-testid="admin-refresh" className="p-2 border border-slate-200 hover:border-primary-brand text-slate-600 hover:text-primary-brand">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-slate-200 hover:border-primary-brand text-slate-700 hover:text-primary-brand px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="flex gap-px bg-slate-200 border border-slate-200 mb-8 flex-wrap">
          {isFullAdmin && (
            <>
              <TabBtn active={tab === "quotes"} onClick={() => setTab("quotes")} icon={ClipboardList} label={`Quote Requests (${quotes.length})`} testid="tab-quotes" />
              <TabBtn active={tab === "shipments"} onClick={() => setTab("shipments")} icon={Package} label={`Shipments (${shipments.length})`} testid="tab-shipments" />
              <TabBtn active={tab === "clients"} onClick={() => setTab("clients")} icon={Users} label={`Clients (${clients.length})`} testid="tab-clients" />
              <TabBtn active={tab === "careers"} onClick={() => setTab("careers")} icon={Briefcase} label={`Careers (${careers.length})`} testid="tab-careers" />
            </>
          )}
          <TabBtn active={tab === "appointments"} onClick={() => setTab("appointments")} icon={CalendarDays} label="Appointments" testid="tab-appointments" />
          {isFullAdmin && (
            <TabBtn active={tab === "team"} onClick={() => setTab("team")} icon={ShieldCheck} label="Team Access" testid="tab-team" />
          )}
        </div>

        {tab === "quotes" && isFullAdmin && <QuotesPanel quotes={quotes} onStatusChange={updateQuoteStatus} onDelete={deleteQuote} />}
        {tab === "shipments" && isFullAdmin && (
          <ShipmentsPanel
            shipments={shipments}
            onStatusChange={updateShipmentStatus}
            onDelete={deleteShipment}
            onCreated={(s) => setShipments((prev) => [s, ...prev])}
          />
        )}
        {tab === "clients" && isFullAdmin && <ClientsPanel clients={clients} />}
        {tab === "careers" && isFullAdmin && <CareersPanel careers={careers} onStatusChange={updateCareerStatus} onDelete={deleteCareer} />}
        {tab === "appointments" && <AppointmentsPanel />}
        {tab === "team" && isFullAdmin && <StaffPanel />}
      </div>
    </div>
  );
}
